// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Button, Card, CardContent, Typography } from '@mui/material';
import { useClientData } from '@shared/advisory/client-profile/providers';
import { AppIcon, IconButton, Input } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { type FC } from 'react';
import { environment } from '@shared/environment';

export const ClientHandoffScreen: FC = () => {
  const { client } = useClientData();

  return (
    <div className="flex flex-col gap-4" data-testid="client-handoff-screen">
      <div className="flex flex-shrink-0 flex-col items-center justify-center gap-2">
        <AppIcon icon="PartyPopper" className="size-8" color="success" />

        <Typography variant="h5" className="!text-xl" data-testid="title">
          <Translation i18nKey="client-handoff-screen.title" ns="components" />
        </Typography>

        <p className="text-center" data-testid="description">
          <Translation
            i18nKey="client-handoff-screen.description"
            ns="components"
            values={{ clientName: client.name }}
          />
        </p>
      </div>

      <Card
        variant="outlined"
        className="!border-secondary/50 !bg-secondary/10"
        data-testid="client-card"
      >
        <CardContent className="!p-4 ">
          <Button
            startIcon={<AppIcon icon="ExternalLink" size="base" />}
            variant="contained"
            color="secondary"
            className="!w-full"
            data-testid="continue-button"
            href={environment.SERVICES.CLIENT_APP_URL!}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Translation
              i18nKey="client-handoff-screen.continue-button"
              ns="components"
            />
          </Button>

          <p className="mt-2 text-center text-sm text-secondary">
            <Translation
              i18nKey="client-handoff-screen.launch-app"
              ns="components"
            />
          </p>
        </CardContent>
      </Card>

      <Card variant="outlined" data-testid="handoff-card">
        <CardContent className="space-y-4 !p-4 text-center">
          <Typography variant="h5">
            <Translation
              i18nKey="client-handoff-screen.mobile-preference"
              ns="components"
            />
          </Typography>

          <div className="grid grid-cols-[1fr_auto] gap-4">
            <Input
              readOnly
              value="https://app.nuvara.com/client/activate?token=abc123-1749579177584"
              className="col-span-1"
              data-testid="url-input"
            />

            <IconButton
              icon="Copy"
              variant="button"
              className="col-span-1"
              color="secondary"
              size="base"
              data-testid="copy-button"
            />

            <div className="col-span-2 grid grid-cols-2 gap-2">
              <Button
                className="col-span-1"
                variant="outlined"
                color="secondary"
                startIcon={<AppIcon icon="Message" size="base" />}
                data-testid="whatsapp-button"
              >
                <Translation i18nKey="common.whatsapp" ns="common" />
              </Button>

              <Button
                className="col-span-1"
                variant="outlined"
                color="secondary"
                startIcon={<AppIcon icon="Mail" size="base" />}
                data-testid="email-button"
              >
                <Translation i18nKey="common.email" ns="common" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card variant="outlined" data-testid="qr-card">
        <CardContent className="space-y-4 !p-4">
          <Typography
            variant="h5"
            className="!flex !items-center !justify-center !gap-1"
          >
            <AppIcon icon="Building" className="size-5" color="gray" />
            <span>
              <Translation
                i18nKey="client-handoff-screen.qr-code-scannable"
                ns="components"
              />
            </span>
          </Typography>

          <div className="flex justify-center">
            <img
              src={`${environment.SERVICES.QR_CODE.BASE_URL}?size=${
                environment.SERVICES.QR_CODE.DEFAULT_SIZE
              }&data=${environment.SERVICES.CLIENT_APP_URL!}`}
              alt="QR Code"
              className="h-52 w-52 border-2 border-dashed border-gray-300"
            />
          </div>

          <p className="text-center">
            <Translation
              i18nKey="client-handoff-screen.qr-code-description"
              ns="components"
            />
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
